import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMeQuery } from "../modules/auth/api/authApi";
import { useGetMyProbationerApplicationsQuery } from "../modules/application/api/probationerApplicationApi";

const STORAGE_KEY = "logbook_auth_user_v2";

const NEEDS_APPLICATION_WHOIS = ["probationer", "student", "technician", "graduate"];

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function OnboardingGuard({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();

  // Reactive localStorage snapshot — re-syncs on auth:updated and cross-tab changes.
  const [stored, setStored] = useState<any>(readStorage);

  const syncStored = useCallback(() => setStored(readStorage()), []);

  useEffect(() => {
    window.addEventListener("auth:updated", syncStored);
    window.addEventListener("storage", syncStored);
    return () => {
      window.removeEventListener("auth:updated", syncStored);
      window.removeEventListener("storage", syncStored);
    };
  }, [syncStored]);

  // /me — primary source for user fields (email_verified_at, changed_password, etc.)
  const { data, isLoading: meLoading, isError } = useMeQuery();

  // Re-sync stored when /me writes fresh data to localStorage
  useEffect(() => {
    if (data) syncStored();
  }, [data, syncStored]);

  // Determine early from stored whether this user needs an application
  // (needed to decide whether to skip the apps query below)
  const isAdminFromStored: boolean = !!(stored?.roles?.some(
    (r: any) => (typeof r === "string" ? r : r?.name) === "admin",
  ));
  // Applicant-grade users must have a probationer application — even if they
  // already have a members-table record (e.g. legacy/imported members with no
  // application). Whether they're actually forced is decided by the live
  // application status below (complete apps bypass the form).
  const needsApplicationFromStored: boolean =
    !isAdminFromStored &&
    NEEDS_APPLICATION_WHOIS.includes(stored?.whois ?? "");

  // Live application list — only fetched for applicant-role users.
  // This is the source of truth for application_status; we do NOT rely on
  // the stale value cached in localStorage (which only updates at login / step 8).
  const { data: appsData, isLoading: appsLoading, isError: appsError } =
    useGetMyProbationerApplicationsQuery(undefined, {
      skip: !stored || isAdminFromStored || !needsApplicationFromStored,
      refetchOnMountOrArgChange: true,
    });

  // Safety valve: never trap the user on the "Syncing profile..." screen if a
  // request stalls. localhost talks to the PROD API, which can be slow/flaky,
  // and the applications query now runs for members too — a hung request must
  // not block the whole app. After a few seconds we proceed with what we have.
  const [bootTimedOut, setBootTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setBootTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, []);

  const isLoading =
    !bootTimedOut &&
    (meLoading ||
      (needsApplicationFromStored && !!stored && appsLoading && !appsError));

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-900 text-white">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
          <p className="text-sm font-medium animate-pulse">
            Syncing profile...
          </p>
        </div>
      </div>
    );
  }

  // Merge stored with fresh /me data — /me takes precedence for user fields,
  // but has_application / application_status come from the live apps query below.
  const user = data?.user
    ? {
        ...stored,
        ...data.user,
      }
    : stored;

  if (!user || isError) {
    return <Navigate to="/login" replace />;
  }

  // Payment callbacks bypass all onboarding checks
  const bypassRoutes = [
    "/payment/processing",
    "/payment/success",
    "/payment/failed",
    "/upgrade/payment-processing",
    "/receipt",
  ];
  if (bypassRoutes.some((r) => location.pathname.startsWith(r))) {
    return <>{children}</>;
  }

  const currentPath = location.pathname;
  const isAdmin = user.roles?.some(
    (r: any) => (typeof r === "string" ? r : r?.name) === "admin",
  );

  // STEP 1: Email Verification
  if (!user.email_verified_at) {
    if (currentPath !== "/verify-email")
      return <Navigate to="/verify-email" replace />;
    return <>{children}</>;
  } else if (currentPath === "/verify-email") {
    return <Navigate to="/change-password" replace />;
  }

  // STEP 2: Change Password
  if (!user.changed_password) {
    if (currentPath !== "/change-password")
      return <Navigate to="/change-password" replace />;
    return <>{children}</>;
  } else if (currentPath === "/change-password") {
    return (
      <Navigate to={isAdmin ? "/dashboard" : "/complete-profile"} replace />
    );
  }

  // --- PROBATIONER / APPLICANT STEPS ---
  if (!isAdmin) {
    // STEP 3: Complete Profile
    if (!user.updated_profile) {
      if (currentPath !== "/complete-profile")
        return <Navigate to="/complete-profile" replace />;
      return <>{children}</>;
    } else if (currentPath === "/complete-profile") {
      return <Navigate to="/pay-application-fee" replace />;
    }

    // STEP 4: Application Fee
    if (!user.application_fee) {
      if (currentPath !== "/pay-application-fee")
        return <Navigate to="/pay-application-fee" replace />;
      return <>{children}</>;
    } else if (currentPath === "/pay-application-fee") {
      return <Navigate to="/first-application" replace />;
    }

    // STEP 5: First Application — live check from the applications API.
    // Applicant-grade users are forced onto the form even if they already have a
    // members-table record (legacy/imported members with no application). The
    // live application status below decides whether they're actually held here.
    const needsApplication = NEEDS_APPLICATION_WHOIS.includes(user.whois ?? "");

    // Only make a routing decision once we actually have the live list. If the
    // applications request errored or hasn't resolved (e.g. it stalled and we
    // timed out of the loading screen), don't force the form — let the user in
    // and re-evaluate on the next load once the request succeeds.
    if (needsApplication && Array.isArray(appsData)) {
      // appsData is the live list from the DB (always fresh on mount).
      const apps = appsData;
      // Every rung the application can sit on once it has left the applicant's
      // hands: waiting on endorsements, with the secretariat, with the
      // Membership Committee, or decided. Naming only some of them is what sent
      // an applicant whose application had moved on back to a form they had
      // already submitted.
      const activeApp =
        apps.find((a: any) =>
          [
            "acknowledged",
            "awaiting_endorsements",
            "under_review",
            "with_committee",
            "submitted",
            "approved",
          ].includes(String(a.status)),
        ) ?? null;

      // Incomplete = no application at all, or the applicant still needs to work
      // on it — status "pending" (not yet submitted) or "returned" (an admin sent
      // it back for corrections). Both mean they should be on the form.
      const applicationIncomplete =
        apps.length === 0 ||
        (!activeApp &&
          apps.every((a: any) =>
            ["pending", "returned"].includes(String(a.status)),
          ));

      if (applicationIncomplete) {
        if (currentPath !== "/first-application")
          return <Navigate to="/first-application" replace />;
        return <>{children}</>;
      } else if (currentPath === "/first-application") {
        return <Navigate to="/dashboard" replace />;
      }
    }
  }

  // Prevent going back to completed onboarding pages
  const onboardingPaths = [
    "/verify-email",
    "/change-password",
    "/complete-profile",
    "/pay-application-fee",
    "/first-application",
  ];
  if (onboardingPaths.includes(currentPath)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
